import { useCallback, type FocusEvent, type RefObject } from 'react';

const GAP_PX = 8;

const scrollParentOf = (element: HTMLElement): HTMLElement | null => {
  for (let node = element.parentElement; node; node = node.parentElement) {
    if (/auto|scroll/.test(getComputedStyle(node).overflowY) && node.scrollHeight > node.clientHeight) return node;
  }
  return document.scrollingElement as HTMLElement | null;
};

const visibleAreaOf = (container: HTMLElement) => {
  if (container === document.scrollingElement) return { top: 0, bottom: window.innerHeight };
  const { top, bottom } = container.getBoundingClientRect();
  return { top, bottom };
};

// Browsers do not scroll a control that is already mostly inside the scrollport, even when a sticky
// footer or the viewport edge cuts it off, and the host page owns the scroll container's scroll-padding.
export const useKeepFocusAboveFooter = (footerRef: RefObject<HTMLElement | null>) =>
  useCallback(
    (event: FocusEvent) => {
      const footer = footerRef.current;
      const target = event.target as HTMLElement;
      // React focus events bubble out of portals (select lists, dialogs) that are not inside the form.
      if (!footer || footer.contains(target) || !event.currentTarget.contains(target)) return;
      const container = scrollParentOf(event.currentTarget as HTMLElement);
      if (!container) return;
      const visible = visibleAreaOf(container);
      const limit = Math.min(footer.getBoundingClientRect().top, visible.bottom);
      const box = target.getBoundingClientRect();
      // A control taller than the visible area keeps its top on screen rather than its bottom.
      const overlap = Math.min(box.bottom - limit + GAP_PX, box.top - visible.top - GAP_PX);
      if (overlap > 0) container.scrollBy({ top: overlap });
    },
    [footerRef],
  );
